<script setup lang="ts">
import type { AvatarIllustration } from '~kui/types/kui';

import { ProgressCircle } from '~ds/components/ui/progress-circle';
import { Heading, Text } from '~ds/components/ui/typography';
import { useI18n } from 'vue-i18n';

interface Props {
  beforeScore: number;
  afterScore: number;
  illustration?: AvatarIllustration;
  animationsEnabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  illustration: undefined,
  animationsEnabled: true,
});

const emit = defineEmits<{
  viewed: [];
}>();

const { t } = useI18n();

// Start at the pre-completion score and animate to the new one, so the ring (and the
// percentage pill) visibly tick up — mirroring the consideration card's level-up motion.
const displayScore = ref(props.animationsEnabled ? props.beforeScore : props.afterScore);
const roundedScore = computed(() => Math.round(displayScore.value));

let timer: ReturnType<typeof setTimeout> | undefined;

const showAvatar = computed(() => {
  return props.illustration !== 'user-silhouette'
});

onMounted(() => {
  emit('viewed');
  if (!props.animationsEnabled) {
    return;
  }
  timer = setTimeout(() => {
    displayScore.value = props.afterScore;
  }, 400);
});

onUnmounted(() => {
  if (timer) {
    clearTimeout(timer);
  }
});
</script>

<template>
  <div class="flex w-full max-w-sm flex-col items-center gap-10">
    <div class="flex flex-col items-center gap-2 text-center">
      <Heading
        size="xl"
        as="h2"
        class="leading-tight"
      >
        {{ t('unified.activity.completion.clarity.heading') }}
      </Heading>
      <Text
        size="lg"
        class="font-semibold"
      >
        {{ t('unified.activity.completion.clarity.subtitle') }}
      </Text>
    </div>
    <div class="flex flex-col items-center">
      <ProgressCircle
        :value="displayScore"
        variant="rounded"
        tone="purple"
        class="size-48"
      >
        <!-- Disc is oversized so its edge tucks under the ring stroke, leaving no gap. -->
        <div class="flex size-40 items-center justify-center rounded-full bg-white">
          <KUICoreAvatar
            v-if="showAvatar"
            class="w-25"
            :illustration="illustration"
            color="white"
          />
          <Heading
            v-else
            size="2xl"
            as="h2"
            class="leading-none"
          >
            {{ roundedScore }}%
          </Heading>
        </div>
      </ProgressCircle>
      <KUICoreBoldTextPill v-if="showAvatar" :text="`${roundedScore}%`" />
    </div>
  </div>
</template>
